// Pure functions for claimed binder (My Binders) operations.
// Extracted from recent-binders.js for testability — no Firebase/localStorage.

import { normalizeToShortUrl } from '../services/recent-binders.js';

/**
 * Pure: decide if a claim should proceed, and return the updated array.
 * @param {Array<{ url: string }>} myBinders - Current claimed binders
 * @param {{ url: string, name: string, owner: string }} binderInfo - Binder to claim
 * @param {number} maxMyBinders - Maximum allowed claimed binders
 * @returns {{ success: boolean, reason?: string, updatedBinders?: Array }}
 */
export function processClaimBinder(myBinders, { url, name, owner }, maxMyBinders) {
  const normalized = normalizeToShortUrl(url);

  // Already claimed?
  if (
    myBinders.some((b) => normalizeToShortUrl(b.url).toLowerCase() === normalized.toLowerCase())
  ) {
    return { success: false, reason: 'already_claimed' };
  }

  // Max limit
  if (myBinders.length >= maxMyBinders) {
    return { success: false, reason: 'max_reached' };
  }

  const updatedBinders = [...myBinders, { url: normalized, name, owner, claimedAt: Date.now() }];
  return { success: true, updatedBinders };
}

/**
 * Pure: remove a binder from the claimed list, return the removed entry.
 * @param {Array<{ url: string }>} myBinders - Current claimed binders
 * @param {string} url - URL to unclaim
 * @returns {{ updatedBinders: Array, removedBinder: Object|null }}
 */
export function processUnclaimBinder(myBinders, url) {
  const normalized = normalizeToShortUrl(url).toLowerCase();

  const removedBinder =
    myBinders.find((b) => normalizeToShortUrl(b.url).toLowerCase() === normalized) || null;

  const updatedBinders = myBinders.filter(
    (b) => normalizeToShortUrl(b.url).toLowerCase() !== normalized
  );

  return { updatedBinders, removedBinder };
}

/**
 * Pure: check if a URL is in the claimed list.
 * @param {Array<{ url: string }>} myBinders
 * @param {string} url
 * @returns {boolean}
 */
export function isBinderClaimed(myBinders, url) {
  const normalized = normalizeToShortUrl(url).toLowerCase();
  return myBinders.some((b) => normalizeToShortUrl(b.url).toLowerCase() === normalized);
}

/**
 * Pure: filter claimed binders from a recent binders list.
 * @param {Array<{ url: string }>} recentBinders
 * @param {Array<{ url: string }>} myBinders
 * @returns {Array} Recent binders that are not claimed
 */
export function filterClaimedFromRecents(recentBinders, myBinders) {
  const myBinderUrls = new Set(myBinders.map((b) => normalizeToShortUrl(b.url).toLowerCase()));
  return recentBinders.filter((b) => !myBinderUrls.has(normalizeToShortUrl(b.url).toLowerCase()));
}

/**
 * Pure: merge and sort binders from multiple sources into a unified sidebar list.
 * Order: trades (starred first) → starred non-trades → regular recents.
 * @param {Object} inputs
 * @param {import('../types.js').RecentBinder[]} inputs.recentBinders
 * @param {Array<{ tradeId: string, binderUrl?: string, binderName?: string, ownerName?: string, updatedAt?: Date | number }>} inputs.openTrades
 * @param {string[]} inputs.pinnedUrls - Normalized pinned/starred binder URLs
 * @param {number} inputs.maxBinders - Maximum entries to return
 * @returns {import('../types.js').RecentBinder[]} Unified, sorted, capped binder list
 */
export function buildUnifiedBinderList({ recentBinders, openTrades, pinnedUrls, maxBinders }) {
  const pinnedUrlSet = new Set(pinnedUrls.map((url) => normalizeToShortUrl(url).toLowerCase()));

  // Build a map of normalized binder URLs to their info
  const recentBinderMap = new Map();
  for (const binder of recentBinders) {
    if (binder.url) {
      recentBinderMap.set(normalizeToShortUrl(binder.url).toLowerCase(), binder);
    }
  }

  // Build set of normalized trade binder URLs for deduplication
  const tradeBinderUrls = new Set();
  for (const trade of openTrades) {
    if (trade.binderUrl) {
      tradeBinderUrls.add(normalizeToShortUrl(trade.binderUrl).toLowerCase());
    }
  }

  // Convert trades to binder format with trade indicator
  const tradeBinders = openTrades.map((trade) => {
    let name = trade.binderName;
    let owner = trade.ownerName;
    const normalizedTradeUrl = trade.binderUrl
      ? normalizeToShortUrl(trade.binderUrl).toLowerCase()
      : '';
    const isStarred = pinnedUrlSet.has(normalizedTradeUrl);
    // Firestore hands back a Date; tests and cached rows may already hold millis.
    const updatedAt = trade.updatedAt;
    const lastAccessed = updatedAt instanceof Date ? updatedAt.getTime() : updatedAt || Date.now();

    if (!name && trade.binderUrl) {
      const recentBinder = recentBinderMap.get(normalizedTradeUrl);
      if (recentBinder) {
        name = recentBinder.name;
        owner = owner || recentBinder.owner;
      }
    }

    return {
      url: `/trades/${trade.tradeId}`,
      binderUrl: normalizeToShortUrl(trade.binderUrl),
      name: name || 'Unknown Binder',
      owner: owner,
      hasTrade: true,
      tradeId: trade.tradeId,
      isStarred: isStarred,
      lastAccessed,
    };
  });

  // Filter recent binders to exclude those with active trades
  const nonTradeBinders = recentBinders.filter((b) => {
    if (!b.url) return true;
    return !tradeBinderUrls.has(normalizeToShortUrl(b.url).toLowerCase());
  });

  // Separate starred vs non-starred binders
  const starredBinders = nonTradeBinders
    .filter((b) => pinnedUrlSet.has(normalizeToShortUrl(b.url).toLowerCase()))
    .map((b) => ({ ...b, isStarred: true }))
    .sort((a, b) => (b.lastAccessed || 0) - (a.lastAccessed || 0));

  const regularBinders = nonTradeBinders
    .filter((b) => !pinnedUrlSet.has(normalizeToShortUrl(b.url).toLowerCase()))
    .sort((a, b) => (b.lastAccessed || 0) - (a.lastAccessed || 0));

  // Sort trades: starred trades first, then by lastAccessed
  const sortedTradeBinders = tradeBinders.sort((a, b) => {
    if (a.isStarred && !b.isStarred) return -1;
    if (!a.isStarred && b.isStarred) return 1;
    return (b.lastAccessed || 0) - (a.lastAccessed || 0);
  });

  // Combine: trades first (starred then non-starred), then starred non-trades, then regular
  const allBinders = [...sortedTradeBinders, ...starredBinders, ...regularBinders];
  return allBinders.slice(0, maxBinders);
}
